import { useState } from "react";
import { Navigate } from "react-router-dom";
import { httpsCallable } from "firebase/functions";
import { KeyRound, Loader2 } from "lucide-react";
import { functions } from "../firebase/config";
import { useCmsAuth } from "../context/CmsAuthContext";
import {
  getPasswordPolicyErrors,
  PASSWORD_POLICY_HINT,
} from "../lib/cmsPasswordPolicy";

function mapErr(err) {
  const msg = err?.message || "No se pudo guardar la contraseña.";
  return msg.replace(/^Firebase:\s*/i, "").replace(/\s*\(.*\)\s*$/, "") || msg;
}

export default function CmsSetPasswordPage() {
  const { user, profile, isCmsStaff, otpVerified, loading, logout } = useCmsAuth();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const mustSet = profile?.mustSetPassword === true;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#fafcfb]">
        <Loader2 className="animate-spin text-brand-primary" />
      </div>
    );
  }

  if (!user || !isCmsStaff) {
    return <Navigate to="/admin/login" replace state={{ from: "/admin/set-password" }} />;
  }

  if (!otpVerified) {
    return <Navigate to="/admin/otp" replace />;
  }

  if (!mustSet && !done) {
    return <Navigate to="/admin" replace />;
  }

  if (done) {
    return <Navigate to="/admin" replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    const policyErrors = getPasswordPolicyErrors(password);
    if (policyErrors.length) {
      setError(`Contraseña débil: ${policyErrors.join("; ")}.`);
      return;
    }
    if (password !== confirm) {
      setError("Las contraseñas no coinciden.");
      return;
    }
    setSaving(true);
    try {
      const fn = httpsCallable(functions, "setCmsOwnPassword");
      await fn({ password });
      setDone(true);
    } catch (err) {
      setError(mapErr(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050e08] flex items-center justify-center px-6 py-16">
      <form
        onSubmit={onSubmit}
        className="w-full max-w-md bg-white rounded-[32px] p-8 shadow-2xl space-y-6"
      >
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-brand-primary/10 flex items-center justify-center text-brand-primary">
            <KeyRound size={22} />
          </div>
          <div>
            <h1 className="font-display text-2xl font-black text-brand-dark">
              Defina su contraseña
            </h1>
            <p className="text-sm text-brand-dark/60">
              Primer acceso de {user.email}. Solo se pide una vez, tras validar el OTP.
            </p>
          </div>
        </div>

        <p className="text-xs text-brand-dark/55 bg-brand-soft/40 rounded-2xl p-3">
          {PASSWORD_POLICY_HINT} No use secuencias como 12345678.
        </p>

        {error && (
          <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-2xl p-4">
            {error}
          </p>
        )}

        <label className="block space-y-2">
          <span className="text-xs font-bold uppercase tracking-widest text-brand-dark/60">
            Nueva contraseña
          </span>
          <input
            type="password"
            required
            minLength={8}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full rounded-2xl border border-brand-soft bg-[#f8faf9] px-4 py-3.5 outline-none focus:ring-2 focus:ring-brand-primary"
            autoComplete="new-password"
          />
        </label>

        <label className="block space-y-2">
          <span className="text-xs font-bold uppercase tracking-widest text-brand-dark/60">
            Confirmar contraseña
          </span>
          <input
            type="password"
            required
            minLength={8}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className="w-full rounded-2xl border border-brand-soft bg-[#f8faf9] px-4 py-3.5 outline-none focus:ring-2 focus:ring-brand-primary"
            autoComplete="new-password"
          />
        </label>

        <button
          type="submit"
          disabled={saving}
          className="w-full bg-brand-primary text-white py-4 rounded-2xl font-bold uppercase tracking-widest text-xs hover:bg-brand-dark transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
        >
          {saving ? <Loader2 className="animate-spin" size={16} /> : null}
          Guardar e ingresar al CMS
        </button>

        <div className="flex justify-end text-xs">
          <button
            type="button"
            onClick={logout}
            className="font-bold text-brand-dark/50 hover:text-brand-dark"
          >
            Cerrar sesión
          </button>
        </div>
      </form>
    </div>
  );
}
